import type { ReactNode } from 'react';
import type { Division } from '@msc-stock/shared';

export function Card({ title, children, actions }: { title?: ReactNode; children: ReactNode; actions?: ReactNode }) {
  return (
    <section className="card">
      {(title || actions) && (
        <div className="row between" style={{ marginBottom: 12 }}>
          {title ? <h2 style={{ margin: 0 }}>{title}</h2> : <span />}
          {actions}
        </div>
      )}
      {children}
    </section>
  );
}

export function Stat({ label, value, sub }: { label: ReactNode; value: ReactNode; sub?: ReactNode }) {
  return (
    <div className="card stat">
      <div className="label">{label}</div>
      <div className="value">{value}</div>
      {sub != null && <div className="sub">{sub}</div>}
    </div>
  );
}

/** HIT / MSC を色で見分けられるようにする。2 つの帳簿を混ぜないのが目的。 */
export function DivisionBadge({ division }: { division: Division | string }) {
  return <span className={`badge ${division === 'HIT' ? 'hit' : 'msc'}`}>{division}</span>;
}

export function Badge({ kind, children }: { kind: 'ok' | 'warn' | 'bad' | 'neutral'; children: ReactNode }) {
  return <span className={`badge ${kind}`}>{children}</span>;
}

export function Field({ label, children }: { label: ReactNode; children: ReactNode }) {
  return (
    <label className="field">
      <span>{label}</span>
      {children}
    </label>
  );
}

export function Notice({ kind, children }: { kind: 'ok' | 'warn' | 'bad'; children: ReactNode }) {
  return <div className={`notice ${kind}`}>{children}</div>;
}

export function Empty({ children }: { children: ReactNode }) {
  return <div className="empty">{children}</div>;
}

export interface BarDatum {
  name: string;
  value: number;
  display?: string;
  tone?: 'hit' | 'msc' | 'accent';
}

/**
 * 横棒グラフ。グラフライブラリを足さずに済ませるため、幅の % だけで描いている。
 * 件数や金額の大小をざっと見るのが目的なので、軸や目盛りは出さない。
 */
export function Bars({ data, limit = 12 }: { data: BarDatum[]; limit?: number }) {
  const shown = data.slice(0, limit);
  const max = Math.max(1, ...shown.map((d) => d.value));
  if (shown.length === 0) return <div className="faint">—</div>;
  return (
    <div className="bars">
      {shown.map((d) => (
        <div className="bar-row" key={d.name}>
          <div className="name" title={d.name}>
            {d.name || '—'}
          </div>
          <div className="bar-track">
            <div
              className={`bar-fill ${d.tone === 'hit' ? 'hit' : d.tone === 'msc' ? 'msc' : ''}`}
              style={{ width: `${Math.max(2, (d.value / max) * 100)}%` }}
            />
          </div>
          <div className="val">{d.display ?? d.value.toLocaleString('en-US')}</div>
        </div>
      ))}
    </div>
  );
}

export function Tabs<T extends string>({
  value,
  options,
  onChange,
}: {
  value: T;
  options: Array<{ value: T; label: ReactNode }>;
  onChange: (v: T) => void;
}) {
  return (
    <div className="tabs">
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          className={o.value === value ? 'active' : ''}
          onClick={() => onChange(o.value)}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}
